import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useStateContext } from '../components/contexts/ContextProvider';
import LoadContentSpinner from '../components/Utils/LoadContentSpinner';
import { Link } from 'react-router-dom';

import { SITE_ORIGIN, siteAssetUrl } from '../config/site';

const CANONICAL_PATH = "/a-propos-de-l'appli-events";
const OG_IMAGE = siteAssetUrl('images/about_app_events.png');

const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Events Billetterie',
    alternateName: 'Events',
    url: SITE_ORIGIN,
    description:
        "Plateforme de billetterie événementielle et d'outils pour organisateurs en Côte d'Ivoire : vente en ligne, tableau de bord et contrôle d'accès.",
};

export default function AboutUs() {
    const { user, loading } = useStateContext();
    const imagePath = process.env.REACT_APP_IMAGE_PATH;

    if (loading) {
        return <LoadContentSpinner />;
    }

    return (
        <>
            <Helmet>
                <title>À propos — Events Billetterie | Billetterie et outils pour organisateurs</title>
                <meta
                    name="description"
                    content="Events Billetterie : application mobile, site web et espace organisateur pour publier, vendre et contrôler l’entrée à vos événements en Côte d’Ivoire. Découvrez notre mission et notre écosystème."
                />
                <meta
                    name="keywords"
                    content="Events Billetterie, billetterie en ligne Côte d'Ivoire, application événements Abidjan, organiser événement, vente de billets, scan QR billet, tableau de bord organisateur"
                />
                <meta name="author" content="Events Billetterie" />
                <meta name="robots" content="index, follow" />

                <meta property="og:title" content="À propos — Events Billetterie" />
                <meta
                    property="og:description"
                    content="Une plateforme tout-en-un pour les participants et les organisateurs : découverte, achat sécurisé, gestion des ventes et contrôle d’accès."
                />
                <meta property="og:image" content={OG_IMAGE} />
                <meta property="og:url" content={`${SITE_ORIGIN}${CANONICAL_PATH}`} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_FR" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="À propos — Events Billetterie" />
                <meta
                    name="twitter:description"
                    content="Billetterie en ligne, app mobile et outils pro pour vos événements en Côte d’Ivoire."
                />
                <meta name="twitter:image" content={OG_IMAGE} />

                <link rel="canonical" href={`${SITE_ORIGIN}${CANONICAL_PATH}`} />
                <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
            </Helmet>

            <main className="relative">
                <div
                    className="nc-BgGlassmorphism absolute inset-x-0 md:top-10 xl:top-40 min-h-0 pl-20 py-24 flex overflow-hidden z-0"
                    data-nc-id="BgGlassmorphism"
                >
                    <span className="block bg-[#5bcfc5] w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-5 lg:w-96 lg:h-96" />
                    <span className="block bg-[#04868b] w-72 h-72 -ml-20 mt-40 rounded-full mix-blend-multiply filter blur-3xl opacity-5 lg:w-96 lg:h-96 nc-animation-delay-2000" />
                </div>

                <div className="container py-16 lg:py-15 space-y-16 lg:space-y-28">
                    <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
                        <div className="flex-shrink-0 lg:mt-0 lg:pl-8">
                            <h1 className="text-3xl !leading-tight font-bold text-gray-900 mb-4 md:text-4xl xl:text-5xl dark:text-neutral-100">
                                La billetterie qui relie organisateurs et public
                            </h1>
                            <div className="block text-base xl:text-lg text-gray-600 dark:text-neutral-400 space-y-4">
                                <p>
                                    <strong>Events Billetterie</strong> est une startup créée et basée en Côte d'Ivoire, elle est une solution de billetterie Simple, 
                                    rapide et professionnelle, accessible gratuitement et permet de créer son événement sur mesure, 
                                    de proposer des billets ou invitations en ligne, de promouvoir son événement et de gérer le contrôle d’accès le jour J.
                                </p>
                                {/* <p>
                                    Côté organisateurs, nous centralisons la publication, la vente, le suivi des commandes et le
                                    contrôle d&apos;entrée — pour vous faire gagner du temps et offrir un parcours acheteur fiable,
                                    du premier clic au scan à l&apos;entrée.
                                </p> */}
                            </div>
                            {/* <div className="flex relative mt-8 justify-center items-center lg:justify-start">
                                <div className="flex flex-wrap gap-3 justify-center">
                                    <a
                                        href="https://play.google.com/store/apps/details?id=com.event.event_app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            alt="Télécharger sur Google Play"
                                            loading="lazy"
                                            width={160}
                                            height={60}
                                            style={{ color: 'transparent', height: '80%' }}
                                            srcSet={`${imagePath}images/btn-android.webp`}
                                        />
                                    </a>
                                    <a
                                        href="https://apps.apple.com/ci/app/events-billetterie/id6738791983"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            alt="Télécharger sur l’App Store"
                                            width={180}
                                            height={60}
                                            loading="lazy"
                                            style={{ color: 'transparent', height: '80%' }}
                                            srcSet={`${imagePath}images/btn-ios.webp`}
                                        />
                                    </a>
                                </div>
                            </div> */}
                        </div>
                        <div
                            className="flex-grow w-full -mt-9"
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <img
                                className="max-w-full sm:!max-w-sm rounded-3xl"
                                alt="Aperçu de Events Billetterie"
                                loading="lazy"
                                decoding="async"
                                style={{ color: 'transparent', width: '85%' }}
                                srcSet={`${imagePath}images/events-banner.jpg`}
                                data-nimg="1"
                            />
                        </div>
                    </div>

                    <section className="sm:shadow-md sm:p-8 rounded-md mx-auto" aria-labelledby="about-mission">
                        <h2 id="about-mission" className="text-2xl font-bold text-gray-900 dark:text-neutral-100">
                            Notre mission
                        </h2>
                        <div className="w-14 py-2 border-b border-neutral-200 dark:border-neutral-700" />
                        <div className="text-gray-600 pt-4 text-base dark:text-neutral-300 space-y-4">
                            <p>
                                Nous voulons rendre la billetterie accessible et professionnelle pour tous : moins de friction pour
                                le public, plus de clarté et de pilotage pour les équipes sur le terrain. Concerts, conférences,
                                soirées, formations ou événements associatifs — le même socle technique pour vendre, encaisser et
                                accueillir sereinement. Travailler avec tout type d’organisateurs nous permet de proposer une solution complète et transversale.
                            </p>
                            <p>
                                Notre mission est de connecter des organisateurs exigeants avec un public qui attend des parcours
                                clairs : informations à jour, paiement en ligne, billet avec QR code et entrées fluides grâce à
                                nos outils de scan.
                            </p>
                            <p>
                                Nous améliorons en continu la plateforme en nous appuyant sur les retours du terrain, les bonnes
                                pratiques de l&apos;événementiel et une exigence forte sur la fiabilité des paiements et des contrôles
                                d&apos;accès.
                            </p>
                            <p className="font-semibold text-primary-700 dark:text-primary-400 pt-2">
                                Merci de faire confiance à Events Billetterie. Explorez les événements, publiez les vôtres, et
                                vivez l&apos;expérience avec nous.
                            </p>
                        </div>
                    </section>

                    <section className="relative" aria-labelledby="about-products">
                        <div className="nc-Section-Heading relative flex flex-col sm:flex-row sm:items-end justify-between lg:mb-8 text-gray-900 dark:text-gray-50">
                            <div className="text-center w-full max-w-4xl mx-auto">
                                <span className="nc-Badge inline-flex px-2.5 py-1 mb-4 rounded-full font-medium text-xs relative text-blue-800 bg-blue-100">
                                    <i className="text-sm las la-star mr-1" />
                                    <i className="text-sm las la-star mr-1" />
                                    <i className="text-sm las la-star mr-1" />
                                    Notre écosystème
                                </span>
                                <h2 id="about-products" className="text-2xl md:text-3xl font-bold">
                                    Une suite d&apos;outils pour tout le cycle de votre événement
                                </h2>
                                <p className="mt-2 md:mt-3 font-normal block text-base text-gray-600 dark:text-gray-400">
                                    Application grand public, espace organisateur sur le web et application de contrôle : trois
                                    briques complémentaires pour couvrir la découverte, la vente et le jour J.
                                </p>
                            </div>
                        </div>
                        <div className="relative grid md:grid-cols-2 gap-10 lg:grid-cols-3 xl:gap-16 mt-6 sm:mx-8">
                            <div className="nc-CardAuthorBox2 h-fit flex flex-col overflow-hidden bg-white dark:bg-neutral-900 rounded-3xl hover:shadow-xl transition-shadow">
                                <div className="relative flex-shrink-0 ">
                                    <div className="nc-NcImage flex aspect-w-7 aspect-h-3 md:aspect-h-4 w-full h-0">
                                        <img
                                            srcSet={`${imagePath}images/Mobile_device_app.png`}
                                            className="object-contain w-full h-full"
                                            alt="Application mobile Events Billetterie sur téléphone"
                                        />
                                    </div>
                                </div>
                                <div className="pt-[10px] px-6 text-center flex flex-col items-center relative -translate-y-7">
                                    <div className="mt-6">
                                        <h3 className="text-lg font-bold">Application — achat de billets</h3>
                                        <p className="block mt-1 text-base text-gray-600 dark:text-neutral-400">
                                            Parcourez l&apos;agenda, choisissez vos tarifs et payez en ligne. Vous recevez vos
                                            billets avec QR code et gardez l&apos;historique de vos commandes sur votre téléphone.
                                        </p>
                                    </div>
                                    <div className="flex justify-between mt-6 gap-4">
                                        <a
                                            className="max-w-12"
                                            href="https://apps.apple.com/ci/app/events-billetterie/id6738791983"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img
                                                srcSet={`${imagePath}images/apple-logo.png`}
                                                loading="lazy"
                                                alt="Apple App Store"
                                                className="image-17"
                                            />
                                        </a>
                                        <a
                                            className="max-w-12"
                                            href="https://play.google.com/store/apps/details?id=com.event.event_app"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img
                                                srcSet={`${imagePath}images/android%20logo.png`}
                                                loading="lazy"
                                                alt="Google Play Store"
                                                className="image-17"
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="nc-CardAuthorBox2 h-fit flex flex-col overflow-hidden bg-white dark:bg-neutral-900 rounded-3xl hover:shadow-xl transition-shadow">
                                <div className="relative flex-shrink-0">
                                    <div className="nc-NcImage flex aspect-w-7 aspect-h-3 md:aspect-h-4 w-full h-0">
                                        <img
                                            srcSet={`${imagePath}images/published-done.png`}
                                            className="object-cover w-full h-full"
                                            alt="Tableau de bord organisateur Events"
                                        />
                                    </div>
                                </div>
                                <div className="pt-[10px] px-6 text-center flex flex-col items-center relative -translate-y-7">
                                    <div className="mt-6">
                                        <h3 className="text-lg font-bold">Espace web — gestion des événements</h3>
                                        <p className="block mt-1 text-base text-gray-600 dark:text-neutral-400">
                                            Créez vos événements, gérez tarifs et quotas, suivez les ventes et pilotez votre
                                            billetterie depuis un tableau de bord centralisé, sans multiplier les outils.
                                        </p>
                                    </div>
                                    {user ? (
                                        <Link
                                            to="/dashboard/events/registration"
                                            className="nc-Button relative h-auto inline-flex items-center justify-center rounded-lg transition-colors text-sm sm:text-sm font-medium px-4 py-2 sm:px-4 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-gray-800 hover:bg-gray-700 text-gray-50 mt-6 sm:mt-6"
                                        >
                                            Commencer à publier
                                        </Link>
                                    ) : (
                                        <Link
                                            to="/create-account"
                                            className="nc-Button relative h-auto inline-flex items-center justify-center rounded-lg transition-colors text-sm sm:text-sm font-medium px-4 py-2 sm:px-4 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-gray-800 hover:bg-gray-700 text-gray-50 mt-6 sm:mt-6"
                                        >
                                            Créer ma billetterie
                                        </Link>
                                    )}
                                </div>
                            </div>

                            <div className="nc-CardAuthorBox2 h-fit flex flex-col overflow-hidden bg-white dark:bg-neutral-900 rounded-3xl hover:shadow-xl transition-shadow">
                                <div className="relative flex-shrink-0 ">
                                    <div className="nc-NcImage flex aspect-w-7 aspect-h-3 md:aspect-h-4 w-full h-0">
                                        <img
                                            srcSet={`${imagePath}images/QR_Scan.png`}
                                            className="object-cover w-full h-full"
                                            alt="Contrôle d’accès par scan de billet"
                                        />
                                    </div>
                                </div>
                                <div className="pt-[10px] px-6 text-center flex flex-col items-center relative -translate-y-7">
                                    <div className="mt-6">
                                        <h3 className="text-lg font-bold">Application — scan et contrôle d&apos;entrée</h3>
                                        <p className="block mt-1 text-base text-gray-600 dark:text-neutral-400">
                                            Accélérez les contrôles à l&apos;entrée : scan fiable des QR codes, files plus courtes
                                            et meilleure expérience pour votre équipe et vos invités.
                                        </p>
                                    </div>
                                    <Link
                                        to="/controle-acces"
                                        className="nc-Button relative h-auto inline-flex items-center justify-center rounded-lg transition-colors text-sm sm:text-sm font-medium px-4 py-2 sm:px-4 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-gray-800 hover:bg-gray-700 text-gray-50 mt-6 sm:mt-6"
                                    >
                                        En savoir plus
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="max-w-5xl container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="bg-gradient-to-br from-[#5bcfc5] to-[#3da8a0] rounded-3xl sm:p-12 p-5 shadow-2xl">
                            <div className="relative flex flex-col items-center sm:max-w-4xl mx-auto">
                                <div className="text-center mt-auto px-4">
                                    <h3 className="text-3xl text-gray-50 font-bold">
                                        Réduisez la complexité technique : une plateforme{' '}
                                        <span className="underline decoration-white/80 underline-offset-4">tout-en-un</span> pour
                                        votre billetterie.
                                    </h3>
                                    <p className="block text-base mt-5 text-gray-100">
                                        Nous accompagnons les organisateurs qui veulent sécuriser leurs ventes, fluidifier
                                        l&apos;entrée et garder une vision claire sur leurs performances — sans empiler les
                                        solutions disparates.
                                    </p>
                                </div>
                            </div>
                            <div className="flex mt-2 justify-center items-center">
                                {user ? (
                                    <a
                                        href="/dashboard/events/registration"
                                        className="nc-Button relative h-auto inline-flex items-center justify-center rounded-lg transition-colors text-sm sm:text-sm font-medium px-4 py-2 sm:px-4 ttnc-ButtonPrimary bg-primary-6000 hover:bg-primary-700 text-white mt-6 sm:mt-6"
                                    >
                                        Créer ma billetterie gratuitement
                                    </a>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="nc-Button relative h-auto inline-flex items-center justify-center rounded-lg transition-colors text-sm sm:text-sm font-medium px-4 py-2 sm:px-4 ttnc-ButtonPrimary bg-primary-6000 hover:bg-primary-700 text-white mt-6 sm:mt-6"
                                    >
                                        Créer ma billetterie gratuitement
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
